<template>
  <div class="configuration p-6">
    <h1 class="text-3xl font-bold mb-6">Configuration des champs conditionnels</h1>
    <table v-if="!errorMessage" class="min-w-full bg-white border border-gray-200">
      <thead>
        <tr>
          <th class="py-2 px-4 border-b border-r">Actions</th>
          <th class="py-2 px-4 border-b border-r">Champ conditionné</th>
          <th class="py-2 px-4 border-b border-r">Champ conditionneur</th>
          <th class="py-2 px-4 border-b">Valeur de condition</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="config in configs" :key="config.id" class="border-b">
          <td class="py-2 px-4 border-r">
            <div class="button-group">
              <button class="bg-blue-500 text-white px-3 py-1 rounded mb-2 w-full"
                @click="updateConfig(config.id)">Modifier</button>
              <button class="bg-red-500 text-white px-3 py-1 rounded w-full"
                @click="deleteConfig(config.id)">Supprimer</button>
            </div>
          </td>
          <td class="py-2 px-4 border-r">
            <select v-model="config.reference_id" class="border p-2 w-full" @change="updateConfig(config.id)">
              <option v-for="section in flattenedSections" :key="section.id" :value="section.id">
                {{ section.name }}
              </option>
            </select>
          </td>
          <td class="py-2 px-4 border-r">
            <select v-model="config.condition_id" class="border p-2 w-full" @change="updateConfig(config.id)">
              <option :value="null">Aucune condition</option>
              <option v-for="section in flattenedSections" :key="section.id" :value="section.id">
                {{ section.name }}
              </option>
            </select>
          </td>
          <td class="py-2 px-4">
            <input v-model="config.condition_value" class="border p-2 w-full" placeholder="Valeur de condition"
              @blur="updateConfig(config.id)" />
          </td>
        </tr>
        <tr v-if="configs.length === 0">
          <td colspan="4" class="py-2 px-4 text-center text-gray-400 italic">Aucune configuration existante</td>
        </tr>
        <!-- Nouvelle configuration -->
        <tr>
          <td class="py-2 px-4 border-r">
            <div class="button-group">
              <button class="bg-green-500 text-white px-3 py-1 rounded w-full" @click="addNewConfig">Ajouter</button>
            </div>
          </td>
          <td class="py-2 px-4 border-r">
            <select v-model="newConfig.reference_id" class="border p-2 w-full">
              <option v-for="section in flattenedSections" :key="section.id" :value="section.id">
                {{ section.name }}
              </option>
            </select>
          </td>
          <td class="py-2 px-4 border-r">
            <select v-model="newConfig.condition_id" class="border p-2 w-full">
              <option :value="null">Aucune condition</option>
              <option v-for="section in flattenedSections" :key="section.id" :value="section.id">
                {{ section.name }}
              </option>
            </select>
          </td>
          <td class="py-2 px-4">
            <input v-model="newConfig.condition_value" class="border p-2 w-full" placeholder="Valeur de condition" />
          </td>
        </tr>
      </tbody>
    </table>
    <p v-else-if="errorMessage" class="text-red-600">{{ errorMessage }}</p>
    <Notification ref="notification" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { sections } from "../../api";
import type { FieldConfigCreate, FieldConfigResponse } from "../../api/field_config";
import { createConfig, deleteConfig, fetchConfigsAdmin, updateConfig } from "../../api/field_config";
import Notification from "../../components/Notification.vue";

// Interfaces pour les types de données
interface Section {
  id: number;
  name: string;
  subsections?: Section[];
}

interface FlattenedSection {
  id: number;
  name: string;
}

interface NotificationComponent {
  showNotification: (message: string, type: string) => void;
}

export default defineComponent({
  name: "Configuration",
  components: {
    Notification,
  },
  data() {
    return {
      configs: [] as FieldConfigResponse[],
      sections: [] as Section[],
      flattenedSections: [] as FlattenedSection[],
      newConfig: {
        reference_id: 0,
        condition_id: undefined,
        condition_value: "",
      } as FieldConfigCreate,
      errorMessage: "",
    };
  },
  mounted(): void {
    this.fetchAllConfigs();
    this.fetchAllSections();
  },
  methods: {
    async fetchAllConfigs(): Promise<void> {
      try {
        this.configs = await fetchConfigsAdmin();
      } catch (error: unknown) {
        this.errorMessage = "Erreur lors de la récupération des configurations.";
        console.error("Erreur lors de la récupération des configurations :", error);
        this.configs = [];
      }
    },
    async fetchAllSections(): Promise<void> {
      try {
        this.sections = await sections.fetchSections();
        this.flattenSections();
      } catch (error) {
        console.error("Erreur lors de la récupération des sections :", error);
      }
    },
    flattenSections(): void {
      const flatten = (sections: Section[], result: FlattenedSection[] = []): FlattenedSection[] => {
        sections.forEach(section => {
          result.push({ id: section.id, name: section.name });
          if (section.subsections && section.subsections.length > 0) {
            flatten(section.subsections, result);
          }
        });
        return result;
      };
      this.flattenedSections = flatten(this.sections);
    },
    async updateConfig(configId: number): Promise<void> {
      if (!configId) {
        this.showNotification("ID de configuration non valide.", "error");
        return;
      }
      const config = this.configs.find(c => c.id === configId);
      if (config) {
        try {
          const updateData = {
            reference_id: config.reference_id,
            condition_id: config.condition_id,
            condition_value: config.condition_value
          };
          await updateConfig(configId, updateData);
          this.showNotification("Configuration mise à jour avec succès.", "success");
          await this.fetchAllConfigs();
        } catch (error) {
          this.showNotification("Erreur lors de la mise à jour de la configuration.", "error");
        }
      }
    },
    async addNewConfig(): Promise<void> {
      try {
        await createConfig(this.newConfig);
        this.showNotification("Configuration ajoutée avec succès.", "success");
        await this.fetchAllConfigs();
        this.newConfig = {
          reference_id: 0,
          condition_id: undefined,
          condition_value: "",
        };
      } catch (error) {
        this.showNotification("Erreur lors de l'ajout de la configuration.", "error");
      }
    },
    async deleteConfig(configId: number): Promise<void> {
      if (!configId) {
        this.showNotification("ID de configuration non valide.", "error");
        return;
      }
      try {
        await deleteConfig(configId);
        this.showNotification("Configuration supprimée avec succès.", "success");
        await this.fetchAllConfigs();
      } catch (error) {
        this.showNotification("Erreur lors de la suppression de la configuration.", "error");
      }
    },
    showNotification(message: string, type: string): void {
      const notification = this.$refs.notification as NotificationComponent;
      notification.showNotification(message, type);
    },
  },
});
</script>

<style scoped>
.button-group {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.button-group button {
  width: 100%;
}
</style>